import React from "react"
import type { CSSProperties, ReactNode } from "react"
import { CloseIcon, SuccessIcon, ErrorIcon, InfoIcon } from "./assets"
import { OldButton } from "../old-button"
import { VisuallyHidden } from "../visually-hidden"
import * as rht from "react-hot-toast"
import type { Toast as RhtToast } from "react-hot-toast"

export type ToastType = "info" | "success" | "error" | "cta"

export type ToastAction = {
  text: string
  onClick?: () => void | Promise<void>
}

export type ToastProps = {
  /** Internal react-hot-toast object. Provided automatically when using the toast() function — do not pass manually. */
  toast?: RhtToast
  /** The type of toast, which determines the color and default icon */
  type?: ToastType
  /** The title text to display in the toast */
  title?: string | ReactNode
  /** The body text or component to display in the toast */
  text: string | ReactNode
  /** Custom icon to display instead of the default type icon */
  icon?:
    | string
    | React.ComponentType<{ style?: CSSProperties; className?: string }>
  /** Action buttons to display in the toast */
  actions?: ToastAction[]
  /** Called when the toast is dismissed */
  handleClose?: () => void
  /** Whether to show a close button on the toast */
  closable?: boolean
}

type ToastOptions = {
  /** Optional unique identifier for the toast. If provided, only one toast with this ID can exist at a time. Calling toast() again with the same ID will update the existing toast. */
  id?: string
  /** Duration in milliseconds before the toast auto-dismisses. Use Infinity to keep the toast open indefinitely. Defaults to auto-dismiss. */
  duration?: number
} & Omit<ToastProps, "toast" | "handleClose">

const ICONS = {
  success: SuccessIcon,
  error: ErrorIcon,
  info: InfoIcon
}

// The convertkit-design build has trouble importing SVGs, so we've formatted
// the default icons as components. On the main app, it's cleaner to import an
// SVG and pass it into an <img src="" />, so we want to support both of these
// use cases. Once ck-design's build supports SVG imports, this can be removed.
type ImageOrSVGProps = {
  icon:
    | string
    | React.ComponentType<{ style?: CSSProperties; className?: string }>
  alt: string
  style?: CSSProperties
  className?: string
}

const ImageOrSVG = ({ icon, alt, ...rest }: ImageOrSVGProps) => {
  // If the icon is a React component...
  if (typeof icon === "function") {
    const IconComponent = icon
    return <IconComponent {...rest} />
  } else {
    // If the icon is imported...
    return <img src={icon} alt={alt} {...rest} />
  }
}

const getBarColor = (type: ToastType): string => {
  switch (type) {
    case "success":
      return "#589D78"
    case "error":
      return "var(--orange-500)"
    case "info":
      return "var(--blue-500)"
    case "cta":
    default:
      return "var(--gray-100)"
  }
}

type ToastIconProps = {
  type: ToastType
  icon?: ToastProps["icon"]
}

const ToastIcon = ({ type, icon }: ToastIconProps) => {
  const barStyles: CSSProperties = {
    backgroundColor: getBarColor(type),
    borderRadius: 6,
    top: 6,
    left: 6,
    bottom: 6,
    width: 4
  }

  const iconStyles: CSSProperties = {
    width: 26,
    height: 26
  }

  const fallbackIcon = ICONS[type as keyof typeof ICONS]

  return (
    <div className="justify-center mx-6 hidden md:flex">
      <div className="absolute inset-y-auto left-0" style={barStyles} />
      <ImageOrSVG
        icon={icon || fallbackIcon}
        alt=""
        style={iconStyles}
        className="ml-2"
      />
    </div>
  )
}

type TextProps = {
  title?: string | ReactNode
  text: string | ReactNode
}

const Text = ({ title, text }: TextProps) => {
  return (
    <div>
      {title && <div className="font-semibold text-gray-900 mb-1">{title}</div>}
      {text && <div className="text-gray-700">{text}</div>}
    </div>
  )
}

type ActionsProps = {
  actions: ToastAction[]
  handleClose?: () => void
}

const Actions = ({ actions, handleClose }: ActionsProps) => (
  <div className="ml-4">
    <div className="flex items-center gap-2">
      {actions.map(action => {
        // Closes the toast after an action button is clicked.
        const handleClick = async () => {
          if (!action.onClick) return
          await action.onClick()
          handleClose?.()
        }

        return (
          <OldButton
            key={action.text}
            is="primary"
            className="whitespace-nowrap"
            {...action}
            onClick={handleClick}
          >
            {action.text}
          </OldButton>
        )
      })}
    </div>
  </div>
)

type CloseProps = {
  handleClose?: () => void
}

const Close = ({ handleClose }: CloseProps) => {
  const closeStyles: CSSProperties = {
    width: 16,
    height: 16
  }

  return (
    <button onClick={handleClose} className="absolute top-0 right-0 p-2 block">
      <CloseIcon className="block" style={closeStyles} />
      <VisuallyHidden>Close notification</VisuallyHidden>
    </button>
  )
}

/**
 * Displays a single toast notification with an icon, optional title, body text, and action buttons.
 * Use the toast() function to trigger toasts programmatically, and place Toaster once in your app root.
 */
export function Toast({
  toast,
  type = "info",
  title,
  text,
  icon,
  actions,
  handleClose,
  closable
}: ToastProps) {
  return (
    <rht.ToastBar
      toast={toast!}
      position="bottom-center"
      style={{ padding: 0, maxWidth: 500 }}
    >
      {() => (
        <div
          role="alert"
          className="rounded bg-white flex items-center text-left relative shadow-lg text-sm p-3 md:pr-8 md:pl-0 md:py-6"
        >
          <ToastIcon type={type} icon={icon} />
          <Text title={title} text={text} />
          {actions && <Actions actions={actions} handleClose={handleClose} />}
          {closable && <Close handleClose={handleClose} />}
        </div>
      )}
    </rht.ToastBar>
  )
}

/**
 * Displays a toast notification
 * @returns The ID of the toast (either the provided ID or an auto-generated one)
 */
export function toast({ id, duration, ...rest }: ToastOptions): string {
  return rht.toast.custom(
    t => (
      <Toast toast={t} handleClose={() => rht.toast.dismiss(t.id)} {...rest} />
    ),
    { id, duration }
  )
}

/**
 * Dismisses a toast notification
 * @param toastId - The ID of the toast to dismiss (either custom ID or the ID returned from toast())
 */
export function dismiss(toastId: string): void {
  rht.toast.dismiss(toastId)
}

export function Toaster() {
  return <rht.Toaster position="bottom-center" />
}
